import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Smartphone, Monitor, CheckCircle, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import PageSEO from "@/components/seo/PageSEO";
import StructuredData from "@/components/seo/StructuredData";
import { useSafeAreaInsets } from "@/hooks/useSafeAreaInsets";
import { cn } from "@/lib/utils";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=app.lovable.a458feef936a4fdd9306ef7eae220648";

const InstallPage = () => {
  const [isInstalled, setIsInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const { topPadding } = useSafeAreaInsets();

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-godyspo-background text-white">
      <PageSEO
        title="Download GoDyspo App | Real Estate Investing on Android"
        description="Get the GoDyspo app for Android. The #1 real estate disposition platform for wholesalers and investors. Find off-market deals and close faster."
        canonical="/install"
        keywords="GoDyspo app, real estate app, wholesale real estate app, property investing app, Android real estate app, off-market deals app"
      />
      <StructuredData type="MobileApplication" />
      
      {/* Header */}
      <header className={cn("border-b border-white/10", topPadding)}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img 
              src="/lovable-uploads/89c54f4a-a59c-4bc2-a97b-9bf1790aea0a.png" 
              alt="GoDyspo" 
              className="h-8"
            />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-white hover:text-godyspo-gold">
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="flex-1 py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-godyspo-gold/10 mb-6">
              <Download className="w-10 h-10 text-godyspo-gold" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Get the GoDyspo <span className="text-godyspo-gold">App</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Find investment properties on the go. Available on Google Play and as a web app.
            </p>
          </div>

          {/* Google Play Store Badge - Prominent */}
          <div className="mb-12 text-center">
            <h2 className="text-lg text-gray-300 mb-4">Download on Android</h2>
            <a 
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block transition-transform hover:scale-105"
            >
              <img 
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                alt="Get it on Google Play"
                className="h-20"
              />
            </a>
            <p className="text-sm text-gray-500 mt-2">
              Free to download • Requires Android 8.0+
            </p>
          </div>

          {isInstalled ? (
            <Card className="bg-card/50 border-godyspo-gold/20 p-8 text-center">
              <CheckCircle className="w-16 h-16 text-godyspo-gold mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Already Installed!</h2>
              <p className="text-gray-400 mb-6">
                GoDyspo is installed on your device. You can access it from your home screen.
              </p>
              <Link to="/">
                <Button className="bg-godyspo-gold text-black hover:bg-godyspo-gold/90">
                  Open GoDyspo
                </Button>
              </Link>
            </Card>
          ) : (
            <>
              {/* Install Button for PWA (Android Chrome) */}
              {deferredPrompt && (
                <div className="mb-12 text-center">
                  <p className="text-gray-400 mb-3">Or install as a web app:</p>
                  <Button 
                    onClick={handleInstallClick}
                    size="lg"
                    className="bg-godyspo-gold text-black hover:bg-godyspo-gold/90 text-lg px-8 py-6"
                  >
                    <Download className="mr-2 h-5 w-5" />
                    Install Web App
                  </Button>
                </div>
              )}

              {/* Benefits */}
              <div className="grid md:grid-cols-3 gap-6 mb-12">
                <Card className="bg-card/50 border-white/10 p-6">
                  <div className="w-12 h-12 rounded-full bg-godyspo-gold/10 flex items-center justify-center mb-4">
                    <Smartphone className="w-6 h-6 text-godyspo-gold" />
                  </div>
                  <h3 className="font-semibold mb-2">Easy Access</h3>
                  <p className="text-sm text-gray-400">
                    Launch directly from your home screen like any native app
                  </p>
                </Card>
                <Card className="bg-card/50 border-white/10 p-6">
                  <div className="w-12 h-12 rounded-full bg-godyspo-gold/10 flex items-center justify-center mb-4">
                    <Monitor className="w-6 h-6 text-godyspo-gold" />
                  </div>
                  <h3 className="font-semibold mb-2">Works Offline</h3>
                  <p className="text-sm text-gray-400">
                    Browse cached properties even without an internet connection
                  </p>
                </Card>
                <Card className="bg-card/50 border-white/10 p-6">
                  <div className="w-12 h-12 rounded-full bg-godyspo-gold/10 flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-godyspo-gold" />
                  </div>
                  <h3 className="font-semibold mb-2">Push Notifications</h3>
                  <p className="text-sm text-gray-400">
                    Get instant alerts for new properties matching your criteria
                  </p>
                </Card>
              </div>

              {/* Platform-specific Instructions */}
              <div className="space-y-8">
                <h2 className="text-2xl font-bold text-center mb-6">Alternative Install Methods</h2>
                
                <Card className="bg-card/50 border-white/10 p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Smartphone className="mr-2 h-5 w-5 text-godyspo-gold" />
                    Install on iPhone (iOS)
                  </h3>
                  <ol className="space-y-3 text-gray-300">
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">1.</span>
                      <span>Open GoDyspo in <strong>Safari</strong> (must be Safari, not Chrome)</span>
                    </li>
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">2.</span>
                      <span>Tap the <strong>Share</strong> button (square with arrow pointing up) at the bottom</span>
                    </li>
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">3.</span>
                      <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
                    </li>
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">4.</span>
                      <span>Tap <strong>"Add"</strong> in the top right corner</span>
                    </li>
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">5.</span>
                      <span>GoDyspo icon will appear on your home screen!</span>
                    </li>
                  </ol>
                </Card>

                <Card className="bg-card/50 border-white/10 p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Smartphone className="mr-2 h-5 w-5 text-godyspo-gold" />
                    Install on Android (Web App)
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Prefer the native app? <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" className="text-godyspo-gold hover:underline inline-flex items-center">Get it on Google Play <ExternalLink className="ml-1 h-3 w-3" /></a>
                  </p>
                  <ol className="space-y-3 text-gray-300">
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">1.</span>
                      <span>Open GoDyspo in <strong>Chrome</strong> or <strong>Firefox</strong></span>
                    </li>
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">2.</span>
                      <span>Look for the <strong>"Install"</strong> banner at the bottom of the screen</span>
                    </li>
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">3.</span>
                      <span>Tap <strong>"Install"</strong> and confirm</span>
                    </li>
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2 opacity-50">OR</span>
                      <span className="opacity-50">Tap the menu (three dots) → <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong></span>
                    </li>
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">4.</span>
                      <span>GoDyspo icon will appear on your home screen!</span>
                    </li>
                  </ol>
                </Card>

                <Card className="bg-card/50 border-white/10 p-6">
                  <h3 className="text-xl font-bold mb-4 flex items-center">
                    <Monitor className="mr-2 h-5 w-5 text-godyspo-gold" />
                    Install on Desktop
                  </h3>
                  <ol className="space-y-3 text-gray-300">
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">1.</span>
                      <span>Open GoDyspo in <strong>Chrome</strong> or <strong>Edge</strong></span>
                    </li>
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">2.</span>
                      <span>Look for the <strong>install icon</strong> (⊕ or computer icon) in the address bar</span>
                    </li>
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">3.</span>
                      <span>Click the icon and select <strong>"Install"</strong></span>
                    </li>
                    <li className="flex">
                      <span className="font-semibold text-godyspo-gold mr-2">4.</span>
                      <span>GoDyspo will open in its own window and appear in your applications!</span>
                    </li>
                  </ol>
                </Card>
              </div>

              <div className="text-center mt-12">
                <p className="text-gray-400 mb-4">
                  Need help? <Link to="/contact" className="text-godyspo-gold hover:underline">Contact our support team</Link>
                </p>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default InstallPage;
